import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StoreGroupsApi } from "apis/StoreGroupsApi";
import { UsersApi } from "apis/UsersApi";
import { noAccessPermissionList } from "constants/menu";
import { StoreGroupTree } from "models/dbModels/other/StoreGroupTree";
import { PermissionsList } from "models/dbModels/permissions/PermissionsList";
import { StoreGroup } from "models/dbModels/storeGroups/StoreGroup";
import { AdminUser, User } from "models/dbModels/users/User";
import { StoreGroupsTreeFilterQuery } from "models/filters/storeGroups/StoreGroupsTreeFilterQuery";
import { UserSearchFilterQuery } from "models/filters/users/UserSearchFilterQuery";
import { UserAccess } from "models/freedomModels/enums/userAccess";
import { ReducerActionType } from "redux/hooks";

export const getAccountsTable = createAsyncThunk("accountsTableSlice/getAccountsTable", async (params?: UserSearchFilterQuery) => {
    const response = await UsersApi.Search(params ?? {});
    return response.data;
});

export const getAccountDetails = createAsyncThunk("accountsTableSlice/getAccountDetails", async (userId: number) => {
    const response = await UsersApi.Search({ ids: [userId] });
    return response.data.data[0];
});

export const createAccount = createAsyncThunk("accountsTableSlice/createAccount", async (params: User) => {
    const response = await UsersApi.Add([params]);
    return response.data;
});

export const updateAccount = createAsyncThunk("accountsTableSlice/updateAccount", async (params: User) => {
    const response = await UsersApi.Update([params]);
    return response.data;
});

const shortStoreGroup = (elements: StoreGroupTree[]) => {
    if (!elements || elements.length === 0) return [];
    const items: ShortendStoreGroup[] = elements.flatMap((i: StoreGroupTree) => [
        { id: i.id, name: i.levelName },
        ...shortStoreGroup(i.children),
    ]);
    return items;
}

export const getStoreGroups = createAsyncThunk("accountsTableSlice/getStoreGroups", async (params?: StoreGroupsTreeFilterQuery) => {
    const response = await StoreGroupsApi.SearchTree(params ?? {});
    const storeGroupsArray = response.data.data.flatMap((i: StoreGroupTree) => [
        { id: i.id, name: i.levelName },
        ...shortStoreGroup(i.children),
    ]);
    return storeGroupsArray;
});

export const getRootStoreGroups = createAsyncThunk("accountsTableSlice/getRootStoreGroups", async () => {
    const response = await StoreGroupsApi.Search({});
    const storeGroupsArray: FlatStoreGroup[] = response.data.data.flatMap((i: StoreGroup) => [
        { id: i.id, name: i.name, ownerId: i.ownerId }
    ]);
    return storeGroupsArray;
});


export const getAccountsTableAdmin = createAsyncThunk("accountsTableSlice/getAccountsTableAdmin", async (params?: UserSearchFilterQuery) => {
    const response = await UsersApi.AdminSearch(params ?? {});
    return response.data;
});

export const adminSearch = createAsyncThunk("usersSlice/adminSearch", async (params: UserSearchFilterQuery) => {
    const response = await UsersApi.AdminSearch(params);
    return response.data;
});

export const adminAdd = createAsyncThunk("usersSlice/adminAdd", async (params: AdminUser[]) => {
    const response = await UsersApi.AdminAdd(params);
    return response.data;
});

export const adminUpdate = createAsyncThunk("usersSlice/adminUpdate", async (params: AdminUser[]) => {
    const response = await UsersApi.AdminUpdate(params);
    return response.data;
});


export interface ShortendStoreGroup {
    id: number;
    name: string;
}

export interface FlatStoreGroup {
    id: number;
    name: string;
    ownerId: number;
}

export type InitialStateType = {
    id: number;
    givenName: string;
    familyName: string;
    email: string;
    ownerId: number;
    storeGroupId: number;
    status: string;
    isPasswordChanged: boolean;
    password: string;
    confirmPassword: string;
    permissions: PermissionsList;
    access: UserAccess;
    comments?: string;
    error: string;
    loading: boolean;
    table: User[];
    loadingTable: boolean;
    storeGroups: ShortendStoreGroup[];
    rootStoreGroups: FlatStoreGroup[];
    storeGroupsList: ShortendStoreGroup[];
    roles: string[];
}

const initialState: InitialStateType = {
    id: 0,
    givenName: "",
    familyName: "",
    email: "",
    ownerId: 0,
    storeGroupId: 0,
    status: "Active",
    isPasswordChanged: false,
    password: "",
    confirmPassword: "",
    permissions: noAccessPermissionList,
    access: 0,
    comments: undefined,
    error: "",
    loading: false,
    table: [],
    loadingTable: false,
    storeGroups: [],
    rootStoreGroups: [],
    storeGroupsList: [],
    roles: [],
}

const accountSlice = createSlice({
    name: "accountSlice",
    initialState: {
        ...initialState
    },
    reducers: {
        updateAccountDetails: (state, action) => {
            return {
                ...state,
                id: action.payload.id,
                givenName: action.payload.givenName,
                familyName: action.payload.familyName,
                email: action.payload.email,
                ownerId: action.payload.ownerId,
                permissions: action.payload.permissions,
                storeGroupId: action.payload.storeGroupId,
                status: action.payload.status,
                access: action.payload.access,
                comments: action.payload.comments
            };
        },
        cleanAccountDetails: (state) => {
            state.familyName = "";
            state.givenName = "";
            state.email = "";
            state.ownerId = 0;
            state.permissions = { ...noAccessPermissionList };
            state.storeGroupId = 0;
            state.status = "Active";
            state.password = "";
            state.confirmPassword = "";
            state.access = 0;
            state.comments = undefined;
        },
        updateAccountField: (state, action: ReducerActionType<InitialStateType>) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createAccount.fulfilled, (state, action) => {
        });
        builder.addCase(updateAccount.fulfilled, (state, action) => {
        });
        builder.addCase(getAccountsTable.pending, (state) => {
            state.loading = true;
            state.table = [];
            state.error = "";
        });
        builder.addCase(getAccountsTable.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.data ? action.payload.data : [];
        });
        builder.addCase(getAccountsTable.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
        builder.addCase(getStoreGroups.fulfilled, (state, action) => {
            state.storeGroups = action.payload;
        });
        builder.addCase(getRootStoreGroups.fulfilled, (state, action) => {
            state.rootStoreGroups = action.payload;
        });
        builder.addCase(getAccountDetails.fulfilled, (state, action) => {
            if (action.payload) {
                state.id = action.payload.id;
                state.givenName = action.payload.givenName;
                state.familyName = action.payload.familyName;
                state.email = action.payload.email;
                state.permissions = action.payload.permissions;
                state.storeGroupId = action.payload.storeGroupId;
                state.status = action.payload.status;
                state.access = action.payload.access;    
                state.comments = action.payload.comments;            
            }
        });
        builder.addCase(adminSearch.fulfilled, (state, action) => {
            state.id = action.payload.data[0].id;
            state.givenName = action.payload.data[0].givenName;
            state.familyName = action.payload.data[0].familyName;
            state.email = action.payload.data[0].email;
            state.ownerId = action.payload.data[0].ownerId;
            state.permissions = action.payload.data[0].permissions;
            state.storeGroupId = action.payload.data[0].storeGroupId;
            state.status = action.payload.data[0].status;
            state.access = action.payload.data[0].access;
            state.comments = action.payload.data[0].comments;
        });

        builder.addCase(getAccountsTableAdmin.pending, (state) => {
            state.loading = true;
            state.table = [];
            state.error = "";
        });
        builder.addCase(getAccountsTableAdmin.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.data ? action.payload.data : [];
        });
        builder.addCase(getAccountsTableAdmin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
    }
});

export const { cleanAccountDetails, updateAccountField } = accountSlice.actions;

export default accountSlice.reducer;
